/*
  Shreya theme — product behaviours.
  Elements: <product-section> (root: modals, variant side effects), <product-gallery>,
  <product-media-player>, <product-lightbox>, <product-sticky-bar>, <pickup-availability>,
  <complementary-products>, <countdown-timer>, <product-gift-recipient>.
  Relies on core.js (Theme, <variant-picker>, <product-form>, <drawer-panel>, <slider-carousel>).
*/
(() => {
  if (!window.Theme || window.Theme.productReady) return;
  Theme.productReady = true;

  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const desktop = () => window.matchMedia('(min-width: 990px)').matches;

  /* Stop anything playing inside an element (native video, YouTube, Vimeo, 3D). */
  const pauseMediaIn = (el) => {
    $$('video', el).forEach((v) => { if (!v.paused) v.pause(); });
    $$('iframe', el).forEach((frame) => {
      const src = frame.src || '';
      if (src.includes('youtube')) frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }), '*');
      else if (src.includes('vimeo')) frame.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), '*');
    });
    $$('model-viewer', el).forEach((m) => { if (typeof m.pause === 'function') m.pause(); });
  };

  /* Scroll a list so that an item becomes visible, without moving the page. */
  const revealInList = (list, item) => {
    if (!list || !item) return;
    const vertical = list.scrollHeight > list.clientHeight + 2 && getComputedStyle(list).overflowY !== 'visible';
    if (vertical) {
      const top = item.offsetTop - list.offsetTop;
      if (top < list.scrollTop || top + item.offsetHeight > list.scrollTop + list.clientHeight) {
        list.scrollTo({ top: top - list.clientHeight / 2 + item.offsetHeight / 2, behavior: 'smooth' });
      }
    } else {
      const left = item.offsetLeft - list.offsetLeft;
      if (left < list.scrollLeft || left + item.offsetWidth > list.scrollLeft + list.clientWidth) {
        list.scrollTo({ left: left - list.clientWidth / 2 + item.offsetWidth / 2, behavior: 'smooth' });
      }
    }
  };

  /* Jump a <slider-carousel> to a slide without the smooth animation. */
  const jumpSlider = (slider, index) => {
    const track = slider && slider.track;
    if (!track) return;
    const previous = track.style.scrollBehavior;
    track.style.scrollBehavior = 'auto';
    slider.scrollToIndex(index);
    requestAnimationFrame(() => { track.style.scrollBehavior = previous; });
  };

  /* ---------- 3D models (Shopify-hosted model viewer + AR) ---------- */
  const xrSetup = { requested: false };
  const setupXR = (root) => {
    const json = $('[data-product-models]', root);
    if (!json || !window.Shopify || !Shopify.loadFeatures) return;
    const register = () => {
      if (!window.ShopifyXR) { document.addEventListener('shopify_xr_initialized', register, { once: true }); return; }
      try { window.ShopifyXR.addModels(JSON.parse(json.textContent)); } catch (_) { /* invalid json */ }
      window.ShopifyXR.setupXRElements();
    };
    Shopify.loadFeatures([{ name: 'shopify-xr', version: '1.0', onLoad: (err) => { if (!err) register(); } }]);
    xrSetup.requested = true;
  };
  const enhanceModel = (model) => {
    if (!window.Shopify || !Shopify.loadFeatures) return;
    Shopify.loadFeatures([{
      name: 'model-viewer-ui',
      version: '1.0',
      onLoad: (err) => { if (!err && window.Shopify.ModelViewerUI) model.ui = new Shopify.ModelViewerUI(model); },
    }]);
  };

  /* ---------- <product-media-player>: loads a video / 3D model on demand ---------- */
  class ProductMediaPlayer extends HTMLElement {
    connectedCallback() {
      const trigger = $('[data-media-play]', this);
      if (trigger) trigger.addEventListener('click', () => this.load());
      // let shoppers rotate 3D models without dragging the slider
      if (this.dataset.type === 'model') this.addEventListener('pointerdown', (e) => { if (this.classList.contains('is-loaded')) e.stopPropagation(); });
    }
    load() {
      if (this.classList.contains('is-loaded')) return;
      const tpl = $('template', this);
      if (!tpl) return;
      this.appendChild(document.importNode(tpl.content, true));
      this.classList.add('is-loaded');
      const video = $('video', this);
      if (video) {
        video.play().catch(() => {});
        video.focus({ preventScroll: true });
      }
      const model = $('model-viewer', this);
      if (model) enhanceModel(model);
    }
  }
  define('product-media-player', ProductMediaPlayer);

  /* ---------- <product-gallery> ---------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.main = $('[data-gallery-main]', this);
      this.track = $('[data-gallery-track]', this);
      this.thumbList = $('[data-thumb-list]', this);
      this.thumbs = $$('[data-thumb]', this);
      this.current = 0;

      this.thumbs.forEach((btn) => btn.addEventListener('click', () => this.show(Number(btn.dataset.thumb))));
      this.addEventListener('slider:change', (e) => { if (e.target === this.main) this.onSlide(e.detail.index); });
      this.addEventListener('click', (e) => {
        const zoomBtn = e.target.closest('[data-zoom-open]');
        if (zoomBtn && this.contains(zoomBtn)) {
          e.preventDefault();
          this.openLightbox(Number(zoomBtn.dataset.zoomOpen), zoomBtn);
        }
      });

      this.unsubscribe = Theme.on('media:select', ({ root, mediaId } = {}) => {
        if (root && root !== document && root.contains && !root.contains(this)) return;
        this.selectMedia(mediaId);
      });

      if (this.dataset.zoom === 'hover' && window.matchMedia('(hover: hover) and (pointer: fine)').matches) this.enableHoverZoom();
      setupXR(this);
    }
    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }
    get slides() { return this.track ? Array.from(this.track.children) : []; }

    show(index, instant) {
      if (!this.main || !this.slides[index]) return;
      if (instant) jumpSlider(this.main, index);
      else this.main.scrollToIndex(index);
      this.onSlide(index);
    }

    selectMedia(mediaId) {
      if (!mediaId) return;
      const slide = this.slides.find((s) => s.dataset.mediaId === String(mediaId));
      if (!slide) return;
      // the desktop grid shows everything at once: bring the variant image to the front
      if (this.dataset.layout === 'grid' && desktop()) {
        if (slide !== this.track.firstElementChild) this.track.prepend(slide);
        return;
      }
      this.show(this.slides.indexOf(slide));
    }

    onSlide(index) {
      if (index === this.current && this._synced) return;
      this._synced = true;
      this.current = index;
      this.slides.forEach((slide, i) => { if (i !== index) pauseMediaIn(slide); });
      this.thumbs.forEach((btn) => {
        const on = Number(btn.dataset.thumb) === index;
        btn.setAttribute('aria-current', on ? 'true' : 'false');
        if (on) revealInList(this.thumbList, btn.parentElement);
      });
    }

    openLightbox(index, trigger) {
      const panel = this.dataset.lightbox && document.getElementById(this.dataset.lightbox);
      if (!panel || typeof panel.open !== 'function') return;
      panel.open(trigger);
      const box = $('product-lightbox', panel);
      if (box) box.goTo(index);
    }

    enableHoverZoom() {
      $$('.pg__media--image', this).forEach((frame) => {
        const img = $('img', frame);
        if (!img) return;
        frame.addEventListener('pointerenter', () => frame.classList.add('is-magnified'));
        frame.addEventListener('pointermove', (e) => {
          const r = frame.getBoundingClientRect();
          img.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
        });
        frame.addEventListener('pointerleave', () => {
          frame.classList.remove('is-magnified');
          img.style.transformOrigin = '';
        });
      });
    }
  }
  define('product-gallery', ProductGallery);

  /* ---------- <product-lightbox>: full-screen zoom view ---------- */
  class ProductLightbox extends HTMLElement {
    connectedCallback() {
      this.slider = $('slider-carousel', this);
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-lightbox-zoom]');
        if (btn) this.toggleZoom(btn, e);
      });
      this.addEventListener('pointermove', (e) => {
        const btn = e.target.closest('[data-lightbox-zoom].is-zoomed');
        if (btn) this.pan(btn, e);
      });
      this.addEventListener('slider:change', () => this.resetZoom());
      const drawer = this.closest('drawer-panel');
      if (drawer) drawer.addEventListener('drawer:close', () => this.resetZoom());
    }
    goTo(index) {
      this.resetZoom();
      requestAnimationFrame(() => jumpSlider(this.slider, index));
    }
    toggleZoom(btn, e) {
      const on = !btn.classList.contains('is-zoomed');
      this.resetZoom();
      if (on) {
        btn.classList.add('is-zoomed');
        this.classList.add('is-zoomed');
        this.pan(btn, e);
      }
    }
    pan(btn, e) {
      const img = $('img', btn);
      if (!img) return;
      const r = btn.getBoundingClientRect();
      const x = Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100));
      const y = Math.min(100, Math.max(0, ((e.clientY - r.top) / r.height) * 100));
      img.style.transformOrigin = `${x}% ${y}%`;
    }
    resetZoom() {
      this.classList.remove('is-zoomed');
      $$('[data-lightbox-zoom].is-zoomed', this).forEach((b) => {
        b.classList.remove('is-zoomed');
        const img = $('img', b);
        if (img) img.style.transformOrigin = '';
      });
    }
  }
  define('product-lightbox', ProductLightbox);

  /* ---------- Stock message for a variant ---------- */
  const updateStock = (el, variant) => {
    const text = $('[data-stock-text]', el);
    const bar = $('[data-stock-bar]', el);
    if (!variant) { el.hidden = true; return; }
    el.hidden = false;
    let map = {};
    try { map = JSON.parse($('[data-stock-map]', el).textContent); } catch (_) { /* no map */ }
    const [qty = 0, managed = null, policy = 'deny'] = map[variant.id] || [];
    const threshold = parseInt(el.dataset.threshold || '10', 10);
    let state = 'in';
    let label = el.dataset.textIn;
    if (!variant.available) { state = 'out'; label = el.dataset.textOut; }
    else if (managed) {
      if (qty <= 0 && policy === 'continue') { state = 'backorder'; label = el.dataset.textBackorder; }
      else if (qty <= threshold) { state = 'low'; label = (el.dataset.textLow || '').replace('[count]', qty); }
    }
    el.dataset.state = state;
    if (text) text.textContent = label;
    if (bar) {
      const pct = !managed ? 100 : Math.max(0, Math.min(100, (qty / threshold) * 100));
      bar.style.setProperty('--stock', `${pct}%`);
    }
  };

  /* Stamp a <template data-portal> (holding one <drawer-panel>) into <body>; returns the panel. */
  const portal = (tpl, id) => {
    const frag = document.importNode(tpl.content, true);
    const panel = $('drawer-panel', frag);
    if (!panel) return null;
    if (id) panel.id = id;
    const old = document.getElementById(panel.id);
    if (old) old.remove();
    document.body.appendChild(frag);
    return panel;
  };

  /* ---------- <product-section>: the [data-product-root] wrapper ---------- */
  class ProductSection extends HTMLElement {
    connectedCallback() {
      // Modals/drawers ship inside <template data-portal> and are stamped into <body>,
      // so no stacking context or overflow can clip them and they connect only once.
      this.portaled = [];
      $$('template[data-portal]', this).forEach((tpl) => {
        const panel = portal(tpl);
        if (panel) this.portaled.push(panel);
      });
      // Re-open a form modal after a submission reloads the page (success or error message).
      this.portaled.forEach((panel) => {
        if ($('[data-auto-open]', panel)) setTimeout(() => panel.open && panel.open(), 350);
      });
      this.onVariant = (e) => this.applyVariant(e.detail && e.detail.variant);
      this.addEventListener('variant:change', this.onVariant);
    }
    disconnectedCallback() {
      this.portaled.forEach((panel) => panel.remove());
      this.portaled = [];
    }
    applyVariant(variant) {
      $$('[data-stock]', this).forEach((el) => updateStock(el, variant));
      $$('[data-sku-wrap]', this).forEach((el) => { el.hidden = !(variant && variant.sku); });
      $$('pickup-availability', this).forEach((el) => el.update(variant));
      $$('product-sticky-bar', this).forEach((el) => el.update(variant));
    }
  }
  define('product-section', ProductSection);

  /* ---------- <product-sticky-bar>: floating quick-add card ----------
     Shows once the main buy buttons have scrolled above the viewport, hides again while
     the footer is on screen, and stays hidden for the session after the shopper closes it. */
  const STICKY_DISMISS_KEY = 'shreya:sticky-buy-dismissed';
  const readDismissed = () => { try { return sessionStorage.getItem(STICKY_DISMISS_KEY) === '1'; } catch (_) { return false; } };
  const saveDismissed = () => { try { sessionStorage.setItem(STICKY_DISMISS_KEY, '1'); } catch (_) { /* storage blocked */ } };

  class ProductStickyBar extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('[data-product-root]');
      this.mainButton = this.root && $('[data-add-button]', this.root);
      this.form = this.mainButton && (this.mainButton.form || this.mainButton.closest('form'));
      this.button = $('[data-sticky-add]', this);
      this.price = $('[data-sticky-price]', this);
      this.image = $('[data-sticky-img]', this);
      this.select = $('[data-sticky-variant]', this);
      this.variantTitle = $('[data-sticky-variant-title]', this);
      this.picker = this.root && $('variant-picker', this.root);
      this.available = this.button ? !this.button.hasAttribute('data-unavailable') : false;
      if (!this.mainButton || !this.button) return;

      this.passed = false;
      this.atFooter = false;
      this.dismissed = readDismissed();

      /* The root is stretched far below the viewport, so the entry only stops intersecting
         once the buttons have left through the top edge (even after a long jump). */
      this.observer = new IntersectionObserver(([entry]) => {
        this.passed = !entry.isIntersecting && entry.boundingClientRect.bottom < 1;
        this.sync();
      }, { rootMargin: '0px 0px 100000px 0px' });
      this.observer.observe(this.mainButton);

      const footer = document.querySelector('.shopify-section-group-footer-group, body > footer, .site-footer');
      if (footer) {
        this.footerObserver = new IntersectionObserver(([entry]) => {
          this.atFooter = entry.isIntersecting;
          this.sync();
        }, { rootMargin: '0px 0px -24px 0px' });
        this.footerObserver.observe(footer);
      }

      this.button.addEventListener('click', () => this.add());
      const dismiss = $('[data-sticky-dismiss]', this);
      if (dismiss) dismiss.addEventListener('click', () => this.dismiss());
      if (this.select) this.select.addEventListener('change', () => this.choose(this.select.value));
    }
    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.footerObserver) this.footerObserver.disconnect();
      document.body.classList.remove('has-sticky-buy');
    }
    sync() { this.toggle(this.passed && !this.atFooter && !this.dismissed); }
    toggle(show) {
      if (this.classList.contains('is-visible') === show && this.inert === !show) return;
      this.classList.toggle('is-visible', show);
      this.setAttribute('aria-hidden', show ? 'false' : 'true');
      this.inert = !show;
      document.body.classList.toggle('has-sticky-buy', show);
    }
    dismiss() {
      this.dismissed = true;
      saveDismissed();
      if (this.contains(document.activeElement)) document.activeElement.blur();
      this.toggle(false);
    }
    /* Add the selected variant, or take the shopper to the options when it can't be bought. */
    async add() {
      if (!this.available || !this.form) { this.revealOptions(); return; }
      if (this.button.classList.contains('is-loading')) return;
      this.button.classList.add('is-loading');
      this.button.setAttribute('aria-busy', 'true');
      try {
        await Theme.cart.add(new FormData(this.form));
      } catch (err) {
        Theme.toast(err.message || Theme.strings.error);
      } finally {
        this.button.classList.remove('is-loading');
        this.button.removeAttribute('aria-busy');
      }
    }
    revealOptions() {
      const target = (this.picker && this.picker.offsetParent ? this.picker : null) || this.mainButton;
      const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'center' });
      const field = $('input:not([type="hidden"]):not([disabled]), select', target) || this.mainButton;
      if (field && field.focus) field.focus({ preventScroll: true });
    }
    /* Mirror the sticky dropdown into the main picker so every block stays in sync. */
    choose(id) {
      if (!this.picker || !this.picker.variants) return;
      const variant = this.picker.variants.find((v) => String(v.id) === String(id));
      if (!variant) return;
      $$('[data-option-group]', this.picker).forEach((group, i) => {
        const value = variant.options[i];
        const select = $('select', group);
        if (select) { select.value = value; return; }
        $$('input[type="radio"]', group).forEach((input) => { input.checked = input.value === value; });
      });
      this.picker.dispatchEvent(new Event('change'));
    }
    update(variant) {
      this.available = !!(variant && variant.available);
      if (this.button) {
        this.button.toggleAttribute('data-unavailable', !this.available);
        this.button.setAttribute('aria-label', this.available ? this.button.dataset.labelAdd : this.button.dataset.labelChoose);
      }
      if (!variant) return;
      if (this.select && this.select.value !== String(variant.id)) this.select.value = String(variant.id);
      if (this.variantTitle) this.variantTitle.textContent = variant.title;
      if (this.price) {
        let html = `<span class="sticky-buy__now">${Theme.formatMoney(variant.price)}</span>`;
        if (variant.compare_at_price > variant.price) html += ` <s>${Theme.formatMoney(variant.compare_at_price)}</s>`;
        this.price.innerHTML = html;
      }
      if (this.image && variant.featured_image) {
        this.image.removeAttribute('srcset');
        this.image.src = `${variant.featured_image.src}${variant.featured_image.src.includes('?') ? '&' : '?'}width=192`;
      }
    }
  }
  define('product-sticky-bar', ProductStickyBar);

  /* ---------- <pickup-availability>: re-rendered per variant ---------- */
  class PickupAvailability extends HTMLElement {
    async update(variant) {
      const drawerId = this.dataset.drawerId;
      const removeDrawer = () => { const old = document.getElementById(drawerId); if (old) old.remove(); };
      if (!variant || !variant.available) {
        this.innerHTML = '';
        removeDrawer();
        return;
      }
      const token = (this.token = Symbol('pickup'));
      const base = (this.dataset.rootUrl || '/').replace(/\/?$/, '/');
      try {
        const res = await fetch(`${base}variants/${variant.id}/?section_id=pickup-availability-drawer`);
        if (!res.ok) throw new Error();
        const doc = Theme.parseHTML(await res.text());
        if (token !== this.token) return;
        const summary = $('[data-pickup-summary]', doc);
        const tpl = $('template[data-portal]', doc);
        this.innerHTML = summary ? summary.outerHTML : '';
        removeDrawer();
        if (tpl) this.drawer = portal(tpl, drawerId);
      } catch (_) {
        if (token === this.token) this.innerHTML = '';
      }
    }
    disconnectedCallback() {
      if (this.drawer) this.drawer.remove();
    }
  }
  define('pickup-availability', PickupAvailability);

  /* ---------- <complementary-products>: Search & Discovery complementary picks ---------- */
  class ComplementaryProducts extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded || !this.dataset.url) return;
      if (!('IntersectionObserver' in window)) { this.load(); return; }
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) { this.observer.disconnect(); this.load(); }
      }, { rootMargin: '0px 0px 400px 0px' });
      // a hidden element has no box, so watch its parent instead
      this.observer.observe(this.parentElement || this);
    }
    disconnectedCallback() { if (this.observer) this.observer.disconnect(); }
    async load() {
      this.dataset.loaded = 'true';
      try {
        const res = await fetch(this.dataset.url);
        if (!res.ok) return;
        const doc = Theme.parseHTML(await res.text());
        const fresh = $(`complementary-products[data-block-id="${this.dataset.blockId}"]`, doc);
        if (fresh && $('[data-comp-item]', fresh)) {
          this.innerHTML = fresh.innerHTML;
          this.hidden = false;
          Theme.init(this);
        }
      } catch (_) { /* recommendations are optional */ }
    }
  }
  define('complementary-products', ComplementaryProducts);

  /* ---------- <countdown-timer data-end="YYYY-MM-DD HH:MM"> ---------- */
  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      const raw = (this.dataset.end || '').trim().replace(' ', 'T');
      this.end = Date.parse(raw.length === 10 ? `${raw}T23:59:59` : raw);
      if (Number.isNaN(this.end)) { this.hidden = true; return; }
      this.units = {};
      $$('[data-unit]', this).forEach((el) => { this.units[el.dataset.unit] = el; });
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    tick() {
      const left = Math.max(0, this.end - Date.now());
      if (left === 0) {
        clearInterval(this.timer);
        if (!(window.Shopify && Shopify.designMode)) this.hidden = true;
      }
      const s = Math.floor(left / 1000);
      const parts = { days: Math.floor(s / 86400), hours: Math.floor((s % 86400) / 3600), minutes: Math.floor((s % 3600) / 60), seconds: s % 60 };
      Object.entries(parts).forEach(([key, val]) => { if (this.units[key]) this.units[key].textContent = String(val).padStart(2, '0'); });
    }
  }
  define('countdown-timer', CountdownTimer);

  /* ---------- <product-gift-recipient>: gift card recipient fields ---------- */
  class ProductGiftRecipient extends HTMLElement {
    connectedCallback() {
      this.toggleInput = $('[data-gcr-toggle]', this);
      this.fields = $('[data-gcr-fields]', this);
      if (!this.toggleInput || !this.fields) return;
      this.toggleInput.addEventListener('change', () => this.sync());
      this.sync();
    }
    sync() {
      const on = this.toggleInput.checked;
      this.fields.hidden = !on;
      $$('input, textarea', this.fields).forEach((el) => { el.disabled = !on; });
      const offset = $('[data-gcr-offset]', this);
      if (offset) offset.value = new Date().getTimezoneOffset().toString();
    }
  }
  define('product-gift-recipient', ProductGiftRecipient);

  /* ---------- <set-builder>: 1 piece / half set / full set quantity choices ----------
     Picking a choice sets the product quantity; typing a quantity selects the matching choice.
     Totals follow the selected variant's price. */
  class SetBuilder extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('[data-product-root]') || document;
      this.inputs = $$('.pb-set__input', this);
      if (!this.inputs.length) return;
      this.price = parseInt(this.dataset.price || '0', 10);
      this.inputs.forEach((input) => input.addEventListener('change', () => { if (input.checked) this.setQuantity(Number(input.value)); }));
      this.onQty = (e) => {
        if (e.target && e.target.name === 'quantity' && this.qtyInput() === e.target) this.syncFromQuantity();
      };
      this.root.addEventListener('change', this.onQty);
      this.root.addEventListener('input', this.onQty);
      this.onVariant = (e) => {
        const v = e.detail && e.detail.variant;
        if (v && typeof v.price === 'number') { this.price = v.price; this.renderTotals(); }
      };
      this.root.addEventListener('variant:change', this.onVariant);
      // a quantity restored by the browser (back/forward) should select the matching choice
      requestAnimationFrame(() => this.syncFromQuantity());
    }
    disconnectedCallback() {
      if (!this.root) return;
      this.root.removeEventListener('change', this.onQty);
      this.root.removeEventListener('input', this.onQty);
      this.root.removeEventListener('variant:change', this.onVariant);
    }
    qtyInput() {
      return $('quantity-input input[name="quantity"], input[name="quantity"]', this.root);
    }
    setQuantity(qty) {
      const input = this.qtyInput();
      if (!input) return;
      const max = input.max ? Number(input.max) : Infinity;
      input.value = String(Math.min(qty, max));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    syncFromQuantity() {
      const input = this.qtyInput();
      if (!input) return;
      const qty = Number(input.value);
      this.inputs.forEach((radio) => { radio.checked = Number(radio.value) === qty; });
    }
    renderTotals() {
      $$('[data-set-total]', this).forEach((el) => {
        el.textContent = Theme.formatMoney(this.price * Number(el.dataset.setTotal));
      });
    }
  }
  define('set-builder', SetBuilder);

  /* ---------- <delivery-estimate>: "delivered between [start] and [end]" in working days ---------- */
  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      const out = $('[data-eta]', this);
      const template = this.dataset.template || '';
      if (!out || !template.includes('[start]')) return;
      const min = parseInt(this.dataset.min || '4', 10);
      const max = Math.max(min, parseInt(this.dataset.max || '8', 10));
      const week = this.dataset.week || 'mon_sat';
      const isWorkingDay = (d) => {
        const day = d.getDay();
        if (week === 'mon_fri') return day !== 0 && day !== 6;
        if (week === 'mon_sat') return day !== 0;
        return true;
      };
      const addWorkingDays = (from, days) => {
        const d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
        let left = days;
        while (left > 0) {
          d.setDate(d.getDate() + 1);
          if (isWorkingDay(d)) left -= 1;
        }
        return d;
      };
      const locale = document.documentElement.lang || 'en';
      let fmt;
      try { fmt = new Intl.DateTimeFormat(`${locale}-IN`, { weekday: 'short', day: 'numeric', month: 'short' }); } catch (_) { fmt = new Intl.DateTimeFormat('en-IN', { weekday: 'short', day: 'numeric', month: 'short' }); }
      const today = new Date();
      const esc = (s) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
      const html = esc(template)
        .replace('[start]', `<strong>${esc(fmt.format(addWorkingDays(today, min)))}</strong>`)
        .replace('[end]', `<strong>${esc(fmt.format(addWorkingDays(today, max)))}</strong>`);
      out.innerHTML = html;
    }
  }
  define('delivery-estimate', DeliveryEstimate);

  /* ---------- Offer code: copy to clipboard ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest && e.target.closest('[data-copy-code]');
    if (!btn) return;
    const code = btn.dataset.copyCode;
    let ok = false;
    try { await navigator.clipboard.writeText(code); ok = true; } catch (_) {
      const field = document.createElement('textarea');
      field.value = code; field.setAttribute('readonly', ''); field.style.cssText = 'position:fixed;opacity:0;top:0;left:0';
      document.body.appendChild(field); field.select();
      try { ok = document.execCommand('copy'); } catch (__) { ok = false; }
      field.remove();
    }
    if (!ok) return;
    const state = $('[data-copy-state]', btn);
    if (state && !btn.dataset.labelCopy) btn.dataset.labelCopy = state.textContent;
    btn.classList.add('is-copied');
    if (state) state.textContent = btn.dataset.labelCopied || 'Copied';
    clearTimeout(btn._copyTimer);
    btn._copyTimer = setTimeout(() => {
      btn.classList.remove('is-copied');
      if (state) state.textContent = btn.dataset.labelCopy;
    }, 2200);
  });

  /* ---------- Rich text tables: wrap so wide spec tables scroll sideways on phones ---------- */
  const wrapTables = (root = document) => {
    $$('.rte table', root).forEach((table) => {
      if (table.parentElement && table.parentElement.classList.contains('rte-table')) return;
      const wrap = document.createElement('div');
      wrap.className = 'rte-table';
      wrap.tabIndex = 0;
      wrap.setAttribute('role', 'region');
      wrap.setAttribute('aria-label', (table.querySelector('caption, th') || {}).textContent || 'Table');
      table.parentNode.insertBefore(wrap, table);
      wrap.appendChild(table);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => wrapTables());
  else wrapTables();
  document.addEventListener('shopify:section:load', (e) => wrapTables(e.target));

  /* Theme editor: open a selected pop-up block's modal so it can be edited in place. */
  document.addEventListener('shopify:block:select', (e) => {
    const trigger = e.target.querySelector && e.target.querySelector('.pb-popup__trigger[data-drawer-open]');
    if (trigger) Theme.openDrawer(trigger.dataset.drawerOpen, trigger);
  });
  document.addEventListener('shopify:block:deselect', (e) => {
    const trigger = e.target.querySelector && e.target.querySelector('.pb-popup__trigger[data-drawer-open]');
    if (trigger) Theme.closeDrawer(trigger.dataset.drawerOpen);
  });
})();
